import { Controller } from "@hotwired/stimulus";

// Donor checkout nudge (DO-REQ-001..004). The suggestion depends on the amount
// the donor currently has chosen, so it is recomputed reactively against the
// server endpoint (Donations::CloserGoalCalculator is the source of truth) and
// shown at most once per checkout. The donation base amount is read from the
// summary's "Your donation" line, which the form's own updateSummary keeps in
// sync regardless of whether the donor used a preset or a custom amount.
export default class extends Controller {
  static targets = ["message", "accept"];
  static values = {
    url: String,
    amountSelector: { type: String, default: "#other-amount" },
    campaignId: Number,
  };

  connect() {
    this.resolved = false;
    this.suggestedAmount = null;
    this.nudgeKind = null;
    this.originalAmount = null;
    this.shownTracked = false;
    this.debounceTimer = null;
    this.donationAmountNode = document.querySelector(".step-summary__donation .amount");
    if (!this.donationAmountNode) return;

    this.observer = new MutationObserver(() => this.scheduleEvaluate());
    this.observer.observe(this.donationAmountNode, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    this.scheduleEvaluate();
  }

  disconnect() {
    this.stopWatching();
  }

  scheduleEvaluate() {
    if (this.resolved) return;
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.evaluate(), 350);
  }

  async evaluate() {
    if (this.resolved) return;

    const amount = this.currentAmount();
    if (!amount || amount < 1) {
      this.hide();
      return;
    }

    try {
      const response = await fetch(`${this.urlValue}?amount=${encodeURIComponent(amount)}`, {
        headers: { Accept: "application/json" },
        credentials: "include",
      });
      if (!response.ok) {
        this.hide();
        return;
      }
      this.present(await response.json(), amount);
    } catch (_e) {
      this.hide();
    }
  }

  present(data, amount) {
    if (this.resolved) return;

    if (data.kind === "exact" && data.suggested_amount != null) {
      this.suggestedAmount = data.suggested_amount;
      this.nudgeKind = "exact";
      this.originalAmount = amount;
      this.messageTarget.innerHTML = this.exactCopy(data, amount);
      this.acceptTarget.textContent = `Add $${this.delta(amount).toFixed(2)}`;
      this.show();
      this.trackShownOnce();
    } else if (data.kind === "round_up" && data.suggested_amount != null) {
      this.suggestedAmount = data.suggested_amount;
      this.nudgeKind = "round_up";
      this.originalAmount = amount;
      this.messageTarget.textContent = `Round up your donation to $${data.suggested_amount.toFixed(2)}?`;
      this.acceptTarget.textContent = `Round up to $${data.suggested_amount.toFixed(2)}`;
      this.show();
      this.trackShownOnce();
    } else {
      this.hide();
    }
  }

  exactCopy(data, amount) {
    const delta = this.delta(amount).toFixed(2);
    if (data.target === "campaign") {
      return `Add <strong>$${delta}</strong> to help the team reach their campaign goal!`;
    }
    return `Add <strong>$${delta}</strong> to help ${this.studentName()} reach their goal!`;
  }

  accept() {
    if (this.suggestedAmount == null) return;
    this.stopWatching();
    this.resolved = true;
    this.track("donor_nudge_accepted");
    this.writeAmount(this.suggestedAmount);
    this.hide();
  }

  decline() {
    this.stopWatching();
    this.resolved = true;
    this.track("donor_nudge_declined");
    this.hide();
  }

  // Routes the suggested amount through the existing custom-amount field so the
  // form's own updateSummary recomputes totals. Never touches payment state.
  writeAmount(value) {
    const labels = Array.from(document.querySelectorAll(".donate-form__radio-button"));
    const otherLabel = labels.find((label) => {
      const input = label.querySelector("input");
      return input && input.value === "Other";
    });

    if (otherLabel) {
      labels.forEach((label) => label.classList.remove("active"));
      otherLabel.classList.add("active");
      const otherInput = otherLabel.querySelector("input");
      if (otherInput) otherInput.checked = true;
    }

    const customWrap = document.querySelector(".donate-form__amount--custom");
    if (customWrap) customWrap.classList.add("active");

    const amountInput = document.querySelector(this.amountSelectorValue);
    if (amountInput) {
      amountInput.disabled = false;
      amountInput.value = Number(value).toFixed(2);
      amountInput.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  delta(amount) {
    return Math.max(this.suggestedAmount - amount, 0);
  }

  studentName() {
    const node = document.querySelector(".progress-bar__content");
    const name = node && node.dataset.userFirstName ? node.dataset.userFirstName : "them";
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  currentAmount() {
    if (!this.donationAmountNode) return 0;
    return parseFloat(this.donationAmountNode.textContent.replace(/[^0-9.]/g, "")) || 0;
  }

  stopWatching() {
    if (this.observer) this.observer.disconnect();
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }

  trackShownOnce() {
    if (this.shownTracked) return;
    this.shownTracked = true;
    this.track("donor_nudge_shown");
  }

  // Reuses the app's existing GTM dataLayer pipeline (DO-REQ-012). No donor PII
  // is sent: only the campaign, nudge kind, and the amounts already known to the
  // analytics layer. No-op when dataLayer is not present (e.g. consent not given).
  track(eventName) {
    if (typeof window === "undefined" || !Array.isArray(window.dataLayer)) return;

    window.dataLayer.push({
      event: eventName,
      donor_nudge_campaign_id: this.hasCampaignIdValue ? this.campaignIdValue : null,
      donor_nudge_kind: this.nudgeKind,
      donor_nudge_suggested_amount: this.suggestedAmount,
      donor_nudge_original_amount: this.originalAmount,
    });
  }

  show() {
    this.element.hidden = false;
  }

  hide() {
    this.element.hidden = true;
  }
}
